<template>
  <div>
    <label :for="id" class="label-wrapper">
      <span>{{ label }}</span>

      <button @click="handleChange" class="invisible" :disabled="disabled">
        <label :for="id" class="label-checkbox">
          <span class="checkmark" :class="{ checked: value }"></span>
        </label>
      </button>
    </label>

    <input type="checkbox" :id="id" :checked="value" disabled="true" />
  </div>
</template>

<script>
import { groupAdvancedOptionsToRenderConfig } from '@/utils/groupAdvancedOptions'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'CheckboxInputGroups',

  props: {
    advanced_option: {
      required: true,
      type: String,
    },

    id: {
      type: String,
      required: true,
    },

    questions_groups_index: {
      required: true,
      type: Number,
    },
  },

  data() {
    return {
      optionConfig: null,
      label: '',
    }
  },

  computed: {
    ...mapGetters(['questionGroups']),

    currentGroup() {
      return this.questionGroups[this.questions_groups_index]
    },

    value() {
      return (
        this.questionGroups[this.questions_groups_index]?.advancedOptions?.[
          this.advanced_option
        ] || false
      )
    },

    disabled() {
      return this.questionGroups[this.questions_groups_index]?.locked
    },
  },

  watch: {
    language() {
      this.label = this.optionConfig.displayText[this.language]
    },
  },

  methods: {
    ...mapActions([
      'changeGroupAdvancedSettings',
      'updateQuestionGroup',
      'setGroupAdvancedSettingsModalConfig',
    ]),

    handleChange() {
      const updateAccessor = !this.value ? 'onEnable' : 'onDisable'

      const toggleAction = this.optionConfig?.toggleAction?.[updateAccessor]

      const updatedGroup = toggleAction?.updater?.(this.currentGroup)

      const confirmationCallback = () => {
        this.changeGroupAdvancedSettings({
          questionGroupIndex: this.questions_groups_index,
          advancedKey: this.advanced_option,
          value: !this.value,
        })
      }

      if (updatedGroup) {
        this.updateQuestionGroup({
          groupIndex: this.questions_groups_index,
          questionGroup: updatedGroup,
        })
      }

      const requireConfirmationModal = toggleAction?.confirmationModal

      if (requireConfirmationModal) {
        this.setGroupAdvancedSettingsModalConfig({
          text: toggleAction?.modalText[this.language],
          confirmationCallback,
        })
      } else {
        confirmationCallback()
      }
    },
  },

  created() {
    this.optionConfig = groupAdvancedOptionsToRenderConfig[this.advanced_option]

    this.label = this.optionConfig.displayText[this.language]
  },
}
</script>

<style scoped lang="scss">
div {
  display: flex;
  align-items: center;
}

.invisible {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;

  &:disabled {
    @include advancedOptionsDisabled;

    .label-checkbox:hover .checkmark:not(.checked) {
      background: transparent;
    }
  }
}

.label-wrapper {
  width: fit-content;
  white-space: nowrap;
  user-select: none;
  color: $gray-10;
  display: flex;
  align-items: center;
  @include tipography(16);
  .light & {
    color: $main;
  }
}

.label-checkbox {
  display: block;
  position: relative;
  cursor: pointer;
  font-size: 22px;
  padding-left: 20px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  /* Optical alignment */
  margin-top: 4px;
}

.checkmark {
  position: relative;
  display: inline-block;
  height: 18px;
  width: 18px;
  background-color: transparent;
  border: 1px solid $white;
  border-radius: 5px;

  .light & {
    border-color: $gray-6;
  }
}

.label-checkbox:hover .checkmark:not(.checked) {
  background-color: $gray-light-2;
}

.checkmark.checked {
  background-color: $blue;
}

.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

.checkmark.checked:after {
  display: block;
  left: 6px;
  top: 2px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

input[type='checkbox'] {
  display: none;
}

input {
  outline: none;
  margin: 0;
  padding: 5px;
  width: 80px;
  border-radius: 4px;
  font-size: 15px;
  color: $gray-10;
  border: 1px solid $gray-6;
  &.text {
    width: 100%;
  }
  &:focus {
    border-color: $gray-7;
  }
}
</style>
